import { PageRequest } from '@/core/request/PageRequest';
import { Moment } from 'moment';
import {
  SupplierClassification,
  SupplierType,
} from '../people/Suppliers/Suppliers.types';
import { PurchaseItem } from './purchaseItem/PurchaseItem.types';
import { Status } from '@/utils/enum/statusEnum';
import {
  InvoiceGovernmentOperation,
  InvoiceGovernmentType,
} from '../invoice/invoice/invoice.enums';

export enum PurchaseOperationDestination {
  internalOperation = 'INTERNAL_OPERATION',
  interstateOperation = 'INTERSTATE_OPERATION',
  foreignOperation = 'FOREIGN_OPERATION',
}

export enum PurchaseDeliveryType {
  recipient = 'RECIPIENT',
  issuer = 'ISSUER',
  freeShipping = 'FREE_SHIPPING',
  the3rd = 'THE_3RD',
}

export enum PurchaseTaxRegime {
  normalRegime = 'NORMAL_REGIME',
  simpleNational = 'SIMPLE_NATIONAL',
  simpleNationalExcess = 'SIMPLE_NATIONAL_EXCESS_SUBLIMIT_GROSS_REVENUE',
  simplesNationalMei = 'SIMPLE_NATIONAL_MEI',
}

export enum PurchaseItemType {
  part = 'PART',
  task = 'TASK',
  consumable = 'CONSUMABLE',
}

export enum PurchaseBoschXmlStatus {
  PENDING = 'PENDING',
  DELIVERED = 'DELIVERED',
  REJECTED = 'REJECTED',
}

export const PurchaseBoschXmlStatusColor: Record<
  PurchaseBoschXmlStatus,
  string
> = {
  PENDING: 'gray',
  DELIVERED: 'green',
  REJECTED: 'red',
};

export const PurchaseBoschXmlStatusLabel: Record<
  PurchaseBoschXmlStatus,
  string
> = {
  PENDING: 'Pendente',
  DELIVERED: 'Enviado',
  REJECTED: 'Rejeitado',
};

export enum LastItemPurchasesOrderColumn {
  RELEASE_DATE = 'RELEASE_DATE',
  ISSUE_DATE = 'ISSUE_DATE',
}

export enum LastItemPurchasesOption {
  releaseDate = 'releaseDate',
  issueDate = 'issueDate',
}

export const LastItemPurchasesColumnConfig: Record<
  string,
  LastItemPurchasesOrderColumn
> = {
  [LastItemPurchasesOption.issueDate]: LastItemPurchasesOrderColumn.ISSUE_DATE,
  [LastItemPurchasesOption.releaseDate]:
    LastItemPurchasesOrderColumn.RELEASE_DATE,
};

export interface PurchaseListResponse {
  code: number;
  id: number;
  company: string;
  supplier: string;
  documentNumber: string;
  releaseDate: string;
  situations: Status;
  hasFinancial: boolean;
  boschMigration: boolean;
  isGrouping: boolean;
  isDeletionDisabled: boolean;
  boschXmlSendStatus: PurchaseBoschXmlStatus;
  xmlPath?: string;
}

export interface purchaseXmlInterface {
  billNumber: string;
  dueDate: string;
  duplicateNumber: string;
  duplicateValue: number;
  id: number;
}

export interface Purchase {
  isGrouping: boolean;
  accountPayableGrouping: number;
  active: boolean;
  additionalData: string;
  applyDeliveryExpensesBcIcmsSt: boolean;
  applyDeliveryExpensesBcIpi: boolean;
  changeRegisteredData: boolean;
  code: number;
  companyId: number;
  individuallyExpenses: boolean;
  deductIcmsUnencumbered: boolean;
  freightBaseIcms: number;
  freightCfopId: number;
  freightCstIcms: string;
  freightInvoice: boolean;
  freightQuotaIcms: number;
  freightValueIcms: number;
  deliveryType: PurchaseDeliveryType;
  deliveryValue: number;
  departureDate: string;
  discountValue: number;
  documentNumber: string;
  documentTypeId: number;
  expensesValue: number;
  groupedIn: number;
  id: number;
  importedPurchase: boolean;
  issueDate: string;
  keyNfe: string;
  modelNf: string;
  operationDestination: PurchaseOperationDestination;
  purchaseItems: PurchaseItems[];
  releaseDate: string;
  seriesNf: string;
  situation: Status;
  supplierDTO: PurchaseSupplier;
  totalAmount: number;
  valueCofins: number;
  valueCofinsSt: number;
  valueFcpStNfc: number;
  valueIcms: number;
  valueIcmsSt: number;
  valueIpi: number;
  valueIssqn: number;
  valueNormalFcp: number;
  valuePis: number;
  valuePisSt: number;
  valuevBC: number;
  hasFinancial: boolean;
  hasPurchaseReturned: boolean;
  boschMigration: boolean;
  isDeletionDisabled: boolean;
  boschDocumentId?: number;
  boschXmlSendDate?: string;
  boschXmlSendStatus?: PurchaseBoschXmlStatus;
  xmlPath?: string;
  hasPurchaseBillingXml: boolean;
  deliveryPrevisionDate?: string;
  governmentType?: InvoiceGovernmentType;
  governmentReducer?: number;
  governmentOperation?: InvoiceGovernmentOperation;
  selectiveTaxTotal?: number;
  ibsCbsBcTotal?: number;
  ibsUfDeferralTotal?: number;
  ibsUfReturnedTaxTotal?: number;
  ibsUfTotal?: number;
  ibsMunicipalDeferralTotal?: number;
  ibsMunicipalReturnedTaxTotal?: number;
  ibsMunicipalTotal?: number;
  ibsTotal?: number;
  ibsPresumedCreditTotal?: number;
  ibsPresumedCreditSuspendedConditionTotal?: number;
  cbsDeferralTotal?: number;
  cbsReturnedTaxTotal?: number;
  cbsTotal?: number;
  cbsPresumedCreditTotal?: number;
  cbsPresumedCreditSuspendedConditionTotal?: number;
  monophasicIbsTotal?: number;
  monophasicCbsTotal?: number;
  monophasicIbsRetentionTotal?: number;
  monophasicCbsRetentionTotal?: number;
  monophasicIbsRetainedTotal?: number;
  monophasicCbsRetainedTotal?: number;
  ibsReversedTotal?: number;
  cbsReversedTotal?: number;
}

export interface PurchaseItems {
  amountReturned: number;
  cfopId: number;
  complementId: number;
  consumableDTO: PurchaseConsumableDTO;
  consumableId: number;
  discount: number;
  id: number;
  manufacturerId: number;
  name: string;
  ncmId: number;
  partDTO: PurchasePartDTO;
  partId: number;
  productCode: number;
  purchaseItemTaxes: PurchaseItemTaxes;
  quantity: number;
  subGroupId: number;
  totalAmount: number;
  type: PurchaseItemType;
  unitMeasurement: string;
  unitaryValue: number;
  workOrderItemId: number;
}

export interface PurchaseConsumableDTO {
  active: boolean;
  barCode: string;
  cfopId: number;
  comment: string;
  id: number;
  imageUrl: string;
  lifespan: number;
  manufacturerId: number;
  manufacturingCode: string;
  name: string;
  ncmId: number;
  sku: string;
  subGroupId: number;
  unit: string;
  unitAlternative: string;
  unitConversionFactor: number;
}

export interface PurchasePartDTO {
  active: boolean;
  barCode: string;
  cfopId: number;
  comment: string;
  id: number;
  imageUrl: string;
  includeInDiagnosis: boolean;
  kmToMaintenance: number;
  lifespan: number;
  localizaCode: string;
  manufacturerId: number;
  manufacturingCode: string;
  name: string;
  ncmId: number;
  sku: string;
  subGroupId: number;
  unit: string;
  unitAlternative: string;
  unitConversionFactor: number;
}

export interface PurchaseItemTaxes {
  additionalValueMarginSt: number;
  baseCofins: number;
  baseCofinsSt: number;
  baseCsll: number;
  baseFcp: number;
  baseFcpSt: number;
  baseIcms: number;
  baseIcmsSt: number;
  baseIcmsStWithheld: number;
  baseInterstateIcms: number;
  baseIpi: number;
  baseIssqn: number;
  basePis: number;
  basePisSt: number;
  calcPercentCofins: boolean;
  calcPercentCofinsSt: boolean;
  calcPercentPis: boolean;
  calcPercentPisSt: boolean;
  csosnIcms: string;
  cstCofins: string;
  cstIcms: string;
  cstIpi: string;
  cstPis: string;
  deliveryValue: number;
  expenseValue: number;
  id: number;
  insuranceValue: number;
  modBcIcms: number;
  modBcIcmsSt: number;
  motDesIcms: number;
  originIcms: number;
  percentFcp: number;
  percentProvisionalParticipation: number;
  quotaBcOp: number;
  quotaCofins: number;
  quotaCofinsSt: number;
  quotaCsll: number;
  quotaDif: number;
  quotaFcp: number;
  quotaFcpSt: number;
  quotaIcms: number;
  quotaIcmsSt: number;
  quotaIcmsUfDestination: number;
  quotaInterstateIcms: number;
  quotaIpi: number;
  quotaIrpj: number;
  quotaIssqn: number;
  quotaMvaSt: number;
  quotaPis: number;
  quotaPisSt: number;
  reductionIcms: number;
  reductionIcmsSt: number;
  taxRegime: PurchaseTaxRegime;
  ufSt: string;
  valueCofins: number;
  valueCofinsSt: number;
  valueCsll: number;
  valueFcp: number;
  valueFcpSt: number;
  valueIcms: number;
  valueIcmsDif: number;
  valueIcmsFcp: number;
  valueIcmsOp: number;
  valueIcmsSt: number;
  valueIcmsStWithheld: number;
  valueIcmsUnencumbered: number;
  valueInterstateDestinationIcms: number;
  valueInterstateOriginIcms: number;
  valueIpi: number;
  valueIrpj: number;
  valueIssqn: number;
  valuePis: number;
  valuePisSt: number;
  valueTotal: number;
  valueUnityCofins: number;
  valueUnityCofinsSt: number;
  valueUnityPis: number;
  valueUnityPisSt: number;
}

export interface PurchaseSupplier {
  address: PurchaseAddress;
  classification: SupplierClassification;
  cnpj: string;
  cpf: string;
  id: number;
  matrixId: number;
  municipalRegistration: string;
  name: string;
  nickName: string;
  note: string;
  stateRegistration: string;
  type: SupplierType;
  supplierPlanAccountId?: number;
}

export interface PurchaseAddress {
  cityId: number;
  complement: string;
  district: string;
  id: number;
  number: string;
  street: string;
  supplierId: number;
  zipCode: string;
}

export interface PurchaseListParams extends PageRequest {
  code?: string;
  documentNumber?: string;
  releaseDate?: string;
  search?: string;
  supplier?: string;
  workOrderNumber?: string;
  companyId?: string;
  situations?: Status;
  isGrouping?: boolean;
  totalAmount?: number;
}

export interface PurchaseCreateFormValues {
  freightInvoice?: boolean;
  applyDeliveryExpensesBcIcmsSt?: boolean;
  applyDeliveryExpensesBcIpi?: boolean;
  individuallyExpenses?: boolean;
  deliveryType?: PurchaseDeliveryType;
  companyId: number;
  supplier: {
    id: number;
    name: string;
  };
  documentTypeId: number;
  documentNumber?: string;
  deliveryValue?: number;
  discountValue?: number;
  totalAmount?: number;
  expensesValue?: number;
  releaseDate?: Moment;
  issueDate: Moment;
  departureDate?: Moment;
  seriesNf?: string;
  keyNfe?: string;
  modelNf?: string;
  operationDestination: PurchaseOperationDestination;
  additionalData?: string;
  code: number;
  deliveryPrevisionDate?: Moment;
  governmentType?: InvoiceGovernmentType;
  governmentReducer?: number;
  governmentOperation?: InvoiceGovernmentOperation;
  selectiveTaxTotal?: number;
  ibsCbsBcTotal?: number;
  ibsUfDeferralTotal?: number;
  ibsUfReturnedTaxTotal?: number;
  ibsUfTotal?: number;
  ibsMunicipalDeferralTotal?: number;
  ibsMunicipalReturnedTaxTotal?: number;
  ibsMunicipalTotal?: number;
  ibsTotal?: number;
  ibsPresumedCreditTotal?: number;
  ibsPresumedCreditSuspendedConditionTotal?: number;
  cbsDeferralTotal?: number;
  cbsReturnedTaxTotal?: number;
  cbsTotal?: number;
  cbsPresumedCreditTotal?: number;
  cbsPresumedCreditSuspendedConditionTotal?: number;
  monophasicIbsTotal?: number;
  monophasicCbsTotal?: number;
  monophasicIbsRetentionTotal?: number;
  monophasicCbsRetentionTotal?: number;
  monophasicIbsRetainedTotal?: number;
  monophasicCbsRetainedTotal?: number;
  ibsReversedTotal?: number;
  cbsReversedTotal?: number;
}

export interface PurchaseCreateRequest {
  freightInvoice?: boolean;
  applyDeliveryExpensesBcIcmsSt?: boolean;
  applyDeliveryExpensesBcIpi?: boolean;
  individuallyExpenses?: boolean;
  deliveryType?: PurchaseDeliveryType;
  companyId: number;
  supplierDTO: {
    id: number;
  };
  documentTypeId: number;
  documentNumber?: string;
  deliveryValue?: number;
  discountValue?: number;
  totalAmount?: number;
  expensesValue?: number;
  releaseDate?: string;
  issueDate: string;
  departureDate?: string;
  seriesNf?: string;
  keyNfe?: string;
  modelNf?: string;
  operationDestination: PurchaseOperationDestination;
  additionalData?: string;
  type?: PurchaseItemType;
  deliveryPrevisionDate?: string;
  governmentType?: InvoiceGovernmentType;
  governmentReducer?: number;
  governmentOperation?: InvoiceGovernmentOperation;
  selectiveTaxTotal?: number;
  ibsCbsBcTotal?: number;
  ibsUfDeferralTotal?: number;
  ibsUfReturnedTaxTotal?: number;
  ibsUfTotal?: number;
  ibsMunicipalDeferralTotal?: number;
  ibsMunicipalReturnedTaxTotal?: number;
  ibsMunicipalTotal?: number;
  ibsTotal?: number;
  ibsPresumedCreditTotal?: number;
  ibsPresumedCreditSuspendedConditionTotal?: number;
  cbsDeferralTotal?: number;
  cbsReturnedTaxTotal?: number;
  cbsTotal?: number;
  cbsPresumedCreditTotal?: number;
  cbsPresumedCreditSuspendedConditionTotal?: number;
  monophasicIbsTotal?: number;
  monophasicCbsTotal?: number;
  monophasicIbsRetentionTotal?: number;
  monophasicCbsRetentionTotal?: number;
  monophasicIbsRetainedTotal?: number;
  monophasicCbsRetainedTotal?: number;
  ibsReversedTotal?: number;
  cbsReversedTotal?: number;
}

export interface PurchaseUpdateRequest {
  id: number;
  freightInvoice?: boolean;
  freightBaseIcms?: number;
  freightCfopId?: number;
  freightCstIcms?: string;
  freightQuotaIcms?: number;
  freightValueIcms?: number;
  applyDeliveryExpensesBcIcmsSt?: boolean;
  applyDeliveryExpensesBcIpi?: boolean;
  individuallyExpenses?: boolean;
  deliveryType?: PurchaseDeliveryType;
  companyId?: number;
  supplierDTO: {
    id: number;
  };
  documentTypeId: number;
  documentNumber?: string;
  deliveryValue?: number;
  discountValue?: number;
  totalAmount?: number;
  expensesValue?: number;
  releaseDate?: string;
  issueDate: string;
  departureDate?: string;
  seriesNf?: string;
  keyNfe?: string;
  modelNf?: string;
  operationDestination?: PurchaseOperationDestination;
  additionalData?: string;
  code: number;
  purchaseItems?: PurchaseItems[];
  deliveryPrevisionDate?: string;
  governmentType?: InvoiceGovernmentType;
  governmentReducer?: number;
  governmentOperation?: InvoiceGovernmentOperation;
  selectiveTaxTotal?: number;
  ibsCbsBcTotal?: number;
  ibsUfDeferralTotal?: number;
  ibsUfReturnedTaxTotal?: number;
  ibsUfTotal?: number;
  ibsMunicipalDeferralTotal?: number;
  ibsMunicipalReturnedTaxTotal?: number;
  ibsMunicipalTotal?: number;
  ibsTotal?: number;
  ibsPresumedCreditTotal?: number;
  ibsPresumedCreditSuspendedConditionTotal?: number;
  cbsDeferralTotal?: number;
  cbsReturnedTaxTotal?: number;
  cbsTotal?: number;
  cbsPresumedCreditTotal?: number;
  cbsPresumedCreditSuspendedConditionTotal?: number;
  monophasicIbsTotal?: number;
  monophasicCbsTotal?: number;
  monophasicIbsRetentionTotal?: number;
  monophasicCbsRetentionTotal?: number;
  monophasicIbsRetainedTotal?: number;
  monophasicCbsRetainedTotal?: number;
  ibsReversedTotal?: number;
  cbsReversedTotal?: number;
}

export interface LastItemPurchasesRequest {
  partId?: number;
  orderByColumn?: LastItemPurchasesOrderColumn;
}

export interface LastConsumablePurchasesRequest {
  consumableId?: number;
  orderByColumn?: LastItemPurchasesOrderColumn;
}

export interface LastItemPurchases {
  code: number;
  id: number;
  idItem: number;
  quantity: number;
  releaseDate: string;
  supplier: string;
  supplierId: number;
  totalAmountItem: number;
  unitaryValue: number;
}

export interface PurchaseAuthorizeRequest {
  purchaseId: number;
  change: boolean;
}

export interface PurchaseImportXmlRequest {
  file: File;
}

export interface PurchaseGroupCreate {
  body: PurchaseGroupCreateBody[];
  documentTypeId: number;
}

export interface PurchaseGroupCreateBody {
  accountPayableGrouping?: number;
  active?: boolean;
  additionalData?: string;
  applyDeliveryExpensesBcIcmsSt?: boolean;
  applyDeliveryExpensesBcIpi?: boolean;
  changeRegisteredData?: boolean;
  code?: number;
  companyId?: number;
  deductIcmsUnencumbered?: boolean;
  freightInvoice?: boolean;
  deliveryType?: PurchaseDeliveryType;
  deliveryValue?: number;
  departureDate?: string;
  discountValue?: number;
  documentNumber?: string;
  documentTypeId?: number;
  expensesValue?: number;
  groupedIn?: number;
  id?: number;
  individuallyExpenses?: boolean;
  issueDate?: string;
  keyNfe?: string;
  modelNf?: string;
  operationDestination?: PurchaseOperationDestination;
  purchaseItems?: PurchaseItems[];
  releaseDate?: string;
  seriesNf?: string;
  situation?: Status;
  supplierDTO?: PurchaseSupplier;
  totalAmount?: number;
  valueCofins?: number;
  valueCofinsSt?: number;
  valueFcpStNfc?: number;
  valueIcms?: number;
  valueIcmsSt?: number;
  valueIpi?: number;
  valueIssqn?: number;
  valueNormalFcp?: number;
  valuePis?: number;
  valuePisSt?: number;
  valuevBC?: number;
}

export interface PurchaseGrouped {
  isGrouping: boolean;
  accountPayableGrouping: number;
  active: boolean;
  additionalData: string;
  applyDeliveryExpensesBcIcmsSt: boolean;
  applyDeliveryExpensesBcIpi: boolean;
  changeRegisteredData: boolean;
  code: number;
  companyId: number;
  individuallyExpenses: boolean;
  deductIcmsUnencumbered: boolean;
  freightBaseIcms: number;
  freightCfopId: number;
  freightCstIcms: string;
  freightInvoice: boolean;
  freightQuotaIcms: number;
  freightValueIcms: number;
  deliveryType: PurchaseDeliveryType;
  deliveryValue: number;
  departureDate: string;
  discountValue: number;
  documentNumber: string;
  documentTypeId: number;
  expensesValue: number;
  groupedIn: number;
  id: number;
  importedPurchase: boolean;
  issueDate: string;
  keyNfe: string;
  modelNf: string;
  operationDestination: PurchaseOperationDestination;
  purchaseItems: PurchaseItem[];
  releaseDate: string;
  seriesNf: string;
  situation: Status;
  supplierDTO: PurchaseSupplier;
  totalAmount: number;
  valueCofins: number;
  valueCofinsSt: number;
  valueFcpStNfc: number;
  valueIcms: number;
  valueIcmsSt: number;
  valueIpi: number;
  valueIssqn: number;
  valueNormalFcp: number;
  valuePis: number;
  valuePisSt: number;
  valuevBC: number;
}

export interface CalculateTaxesPurchaseFreightInvoice {
  purchaseId: number;
  freightBaseIcms: number;
  freightQuotaIcms: number;
}

export interface PurchaseAllXMLRequest {
  companyId: number;
  /**
   * Format: YYYY-MM-DD
   */
  startDate: string;
  /**
   * Format: YYYY-MM-DD
   */
  endDate: string;
}

export interface PurchaseUploadXmlFileRequest {
  purchaseId: number;
  file: File;
}
